import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ChartFilterModel, ChartFilterPayload, DatasetID } from '../../models/filter.model';
import { getFilterValueAsNumberIfSuch } from 'common/utils/filter.utils';

// identifier
export const CHART_FILTER_SLICE_ID = 'chartFilter';

interface ChartFilterStateModel {
  [key: string]: { filters: ChartFilterModel[] };
}

// default state
const initialState: ChartFilterStateModel = {};

//slice
const chartFilterSlice = createSlice({
  name: CHART_FILTER_SLICE_ID,
  initialState,
  reducers: {
    addChartFilter: (state: ChartFilterStateModel, action: PayloadAction<ChartFilterPayload>) => {
      const { datasetId, chartFilter } = action.payload;
      if (!state[datasetId]) {
        state[datasetId] = { filters: [] };
      }

      const index = state[datasetId].filters.findIndex(
        (filter: ChartFilterModel) =>
          filter.id == chartFilter.id && filter.propertyName == chartFilter.propertyName
      );

      if (index === -1) {
        state[datasetId].filters?.push({
          ...chartFilter,
          value: getFilterValueAsNumberIfSuch(chartFilter.value),
        });
      } else {
        state[datasetId].filters[index].value = getFilterValueAsNumberIfSuch(chartFilter.value);
        state[datasetId].filters[index].propertyDisplayValue = chartFilter.propertyDisplayValue;
      }
    },
    removeChartFilter: (
      state: ChartFilterStateModel,
      action: PayloadAction<
        { index: number; propertyName: string; value: string | number } & DatasetID
      >
    ) => {
      const { datasetId, propertyName, value } = action.payload;

      if (state[datasetId]) {
        const filterIndex = state[datasetId].filters?.findIndex(
          (filter) =>
            filter.propertyName === propertyName && filter.value.toString() === value.toString()
        );
        state[datasetId].filters?.splice(filterIndex, 1);
      }
    },
    // Remove chart filter by widget id and property name
    removeChartFilterById: (
      state: ChartFilterStateModel,
      action: PayloadAction<{ widgetId: string; propertyName: string } & DatasetID>
    ) => {
      const { datasetId, widgetId, propertyName } = action.payload;
      if (state[datasetId]) {
        state[datasetId].filters = state[datasetId].filters.filter(
          (filter: ChartFilterModel) =>
            filter.id !== widgetId && filter.propertyName !== propertyName
        );
      }
    },
    clearChartFilters: (
      state: ChartFilterStateModel,
      action: PayloadAction<Partial<DatasetID>>
    ) => {
      if (action.payload.datasetId) {
        const { datasetId } = action.payload;
        if (state[datasetId]) {
          state[datasetId].filters = [];
        }
      } else {
        Object.keys(state).forEach((key: string) => {
          state[key] = { filters: [] };
        });
      }
    },
    // Toggle alignment filters for Sankey widget because it has two properties to filter
    toggleAlignmentFilters: (
      state,
      action: PayloadAction<{
        datasetId: string;
        filters: ChartFilterModel[];
        replace?: boolean;
      }>
    ) => {
      const { datasetId, filters, replace } = action.payload;

      if (!state[datasetId]) {
        state[datasetId] = { filters: [] };
      }

      if (replace) {
        state[datasetId].filters = state[datasetId].filters.filter((f) => f.id !== filters[0]?.id);

        state[datasetId].filters.push(...filters);
        return;
      }
      const allExist = filters.every((f) =>
        state[datasetId].filters.some(
          (existing) =>
            existing.id === f.id &&
            existing.propertyName === f.propertyName &&
            existing.value === f.value &&
            existing.uniqueCohort === f.uniqueCohort
        )
      );
      if (allExist) {
        state[datasetId].filters = state[datasetId].filters.filter(
          (existing) =>
            !filters.some(
              (f) =>
                f.id === existing.id &&
                f.propertyName === existing.propertyName &&
                f.value === existing.value &&
                f.uniqueCohort === existing.uniqueCohort
            )
        );
      } else {
        state[datasetId].filters = state[datasetId].filters.filter(
          (existing) =>
            !filters.some(
              (f) =>
                f.id === existing.id &&
                f.propertyName === existing.propertyName &&
                f.uniqueCohort === existing.uniqueCohort
            )
        );

        state[datasetId].filters.push(...filters);
      }
    },
  },
});

//selectors

export const selectChartFilters = createSelector(
  [(state: RootState) => state.chartFilter, (state: RootState, datasetId: string) => datasetId],
  (chartFilter, datasetId) => {
    return chartFilter[datasetId] ? chartFilter[datasetId].filters : [];
  }
);

export const selectChartFilterByWidgetId = createSelector(
  [
    (state: RootState) => state.chartFilter,
    (state: RootState, datasetId: string) => datasetId,
    (state: RootState, datasetId: string, widgetId: string) => widgetId,
  ],
  (chartFilter, datasetId, widgetId) => {
    return chartFilter[datasetId]
      ? chartFilter[datasetId].filters.filter((filter: ChartFilterModel) => filter.id === widgetId)
      : null;
  }
);

export const selectAllChartFilters = (state: RootState) => state.chartFilter;
//actions
export const {
  addChartFilter,
  removeChartFilter,
  removeChartFilterById,
  clearChartFilters,
  toggleAlignmentFilters,
} = chartFilterSlice.actions;

//reducer
export default chartFilterSlice;
